// ARCA Payment Service - Handles all payment processing for donations and store purchases
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class ArcaPaymentService {
  // Get authorization headers
  getAuthHeaders() {
    const token = localStorage.getItem('token') || localStorage.getItem('userToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Process a donation through Givebutter
   * @param {Object} donationData - Donation information
   * @returns {Promise<Object>} Response data
   */
  async processDonation(donationData) {
    try {
      const response = await axios.post(
        `${API_URL}/givebutter/donations`,
        donationData
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error processing donation:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to process donation',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Process a store purchase
   * @param {Object} orderData - Order information
   * @returns {Promise<Object>} Response data
   */
  async processStorePurchase(orderData) {
    try {
      // First create the order
      const orderResponse = await axios.post(
        `${API_URL}/store/orders`,
        orderData,
        { headers: this.getAuthHeaders() }
      );
      
      if (orderResponse.data.success) {
        // Then process payment for the order
        const paymentResponse = await axios.post(
          `${API_URL}/store/orders/${orderResponse.data.data.order.id}/payment`,
          {
            orderId: orderResponse.data.data.order.id,
            paymentData: {
              amount: orderData.total_amount || orderResponse.data.data.order.total_amount,
              currency: orderData.currency || 'USD',
              paymentMethod: orderData.payment_method || 'card'
            }
          },
          { headers: this.getAuthHeaders() }
        );
        
        return {
          success: true,
          data: {
            order: orderResponse.data.data.order,
            payment: paymentResponse.data.data.payment
          }
        };
      } else {
        return {
          success: false,
          message: orderResponse.data.message || 'Failed to create order'
        };
      }
    } catch (error) {
      console.error('Error processing store purchase:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to process purchase',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Get supported currencies
   * @returns {Promise<Object>} Response data
   */
  async getSupportedCurrencies() {
    try {
      const response = await axios.get(`${API_URL}/payment/currencies`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching currencies:', error);
      // Return fallback currencies if API fails
      return {
        success: true,
        data: {
          currencies: [
            { code: 'USD', name: 'US Dollar', symbol: '$' },
            { code: 'EUR', name: 'Euro', symbol: '€' },
            { code: 'GBP', name: 'British Pound', symbol: '£' },
            { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
            { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
            { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵' },
            { code: 'ZAR', name: 'South African Rand', symbol: 'R' }
          ]
        }
      };
    }
  }

  /**
   * Get available payment methods
   * @param {string} region - Region code
   * @returns {Promise<Object>} Response data
   */
  async getPaymentMethods(region = 'Global') {
    try {
      const response = await axios.get(`${API_URL}/payment/methods`, {
        params: { region }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch payment methods',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Convert currency
   * @param {number} amount - Amount to convert
   * @param {string} fromCurrency - Source currency
   * @param {string} toCurrency - Target currency
   * @returns {Promise<Object>} Response data
   */
  async convertCurrency(amount, fromCurrency, toCurrency) {
    try {
      const response = await axios.get(`${API_URL}/payment/convert`, {
        params: { amount, from: fromCurrency, to: toCurrency }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error converting currency:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to convert currency',
        error: error.response?.data || error.message
      };
    }
  }
}

export default new ArcaPaymentService();